import React from 'react'
import { UseFormRegister, FieldError, Path, FieldValues } from 'react-hook-form'
import { HTMLSelect as Select } from '@/components/ui/Select'
import { AlertCircle } from 'lucide-react'

interface Option {
  value: string | number
  label: string
}

interface FormSelectProps<TFieldValues extends FieldValues> {
  label: string
  name: Path<TFieldValues>
  register: UseFormRegister<TFieldValues>
  error?: FieldError
  required?: boolean
  placeholder?: string
  disabled?: boolean
  helperText?: string
  className?: string
  options: Option[]
  onChange?: (value: string) => void
}

export function FormSelect<TFieldValues extends FieldValues>({
  label,
  name,
  register,
  error,
  required,
  placeholder = 'Selecione uma opção',
  disabled,
  helperText,
  className,
  options,
  onChange,
}: FormSelectProps<TFieldValues>) {
  return (
    <div className={className}>
      <label htmlFor={String(name)} className="block text-sm font-medium text-brand-gray mb-2">
        {label} {required && <span className="text-error">*</span>}
      </label>

      <select
        id={String(name)}
        disabled={disabled}
        className={`w-full px-4 py-2 bg-brand-gray/10 border rounded-lg text-white focus:outline-none focus:border-brand-primary transition-colors ${
          error ? 'border-error' : 'border-brand-gray/20'
        }`}
        {...register(name, {
          onChange: onChange ? (e) => onChange(e.target.value) : undefined,
        })}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {error && (
        <div className="flex items-center gap-2 mt-2 text-error text-sm">
          <AlertCircle className="w-4 h-4" />
          {error.message}
        </div>
      )}

      {helperText && !error && (
        <p className="text-xs text-brand-gray mt-1">{helperText}</p>
      )}
    </div>
  )
}
